/**
 * Aktivierungen (R2-Punkt 3) — je Anlagenkonto die Quartalssummen der aktivierten
 * Investitionen, UNTER dem BWA-Drilldown. Zeigt NUR abgeschlossene Quartale
 * (status closed; aktuell Q1).
 *
 * HARTE D-040-Regel: ableitbar=false / wert=null ⇒ „nicht ableitbar" (grau),
 * NIEMALS 0 rendern (0,00 € ≠ leer). Ebenso ytd_anfang/summe_ist null ⇒ „—".
 * Leere/fehlende konten ⇒ nichts rendern.
 */
import type { Aktivierungen as AktType, QuarterKey } from '../types/snapshot';
import { DASH, formatEur } from '../lib/format';

const Q_LABEL: Record<string, string> = {
  q1_2026: 'Q1',
  q2_2026: 'Q2',
  q3_2026: 'Q3',
  q4_2026: 'Q4',
};

const Q_ORDER: QuarterKey[] = ['q1_2026', 'q2_2026', 'q3_2026', 'q4_2026'];

/** „nicht ableitbar"-Zelle (grau) — bewusst KEIN 0-Wert (D-040). */
function NichtAbleitbar() {
  return <span className="text-text-muted italic">nicht ableitbar</span>;
}

/**
 * A-6/A-8: Spaltenkopf und seine Erklärung stehen in EINER Quelle. Ein Kopf, der
 * eine Semantik behauptet, und eine Fußnote, die sie an anderer Stelle beschreibt,
 * sind zwei Quellen für eine Aussage (Defektmuster 1).
 */
export const AKT_TEXTE = {
  summeIstTitle:
    'Σ der bisher ausgewiesenen Ist-Monate — KEIN Jahreswert. Der YTD-Anfang ' +
    '(nicht monatlich aufteilbarer Zugang bis zum ersten Stichtag) ist hier NICHT ' +
    'enthalten und steht in der Spalte links. Voller Zugang = YTD-Anfang + Σ Ist-Monate.',
  summeIstFussnote:
    '* „Σ Ist-Monate" ist kein Jahreswert: die Spalte summiert nur die bisher ' +
    'ausgewiesenen Monate und enthält den YTD-Anfang nicht. Der volle Zugang ist ' +
    'YTD-Anfang + Σ Ist-Monate.',
} as const;

export interface AktivierungenProps {
  aktivierungen?: AktType;
  /**
   * Jüngster Ist-Stichtag („MM/2026") aus dem Snapshot (cashflow.ytd.stichtag).
   * Fehlt er, entfällt die Stichtagsangabe im Kopf — die Semantik-Aussage bleibt.
   */
  stichtag?: string;
}

export function Aktivierungen({ aktivierungen, stichtag }: AktivierungenProps) {
  const konten = aktivierungen?.konten ?? [];
  if (konten.length === 0) return null;

  // Nur tatsächlich vorhandene (= abgeschlossene) Quartale als Spalten, sortiert.
  const quarterKeys = Q_ORDER.filter((qk) => konten.some((k) => k.quartale?.[qk] !== undefined));

  return (
    <section aria-label="Aktivierungen (Anlagevermögen)">
      <h3 className="mb-3 text-lg font-semibold text-text">Aktivierungen (Anlagevermögen)</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-surface p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-text-muted">
              <th scope="col" className="py-2 font-medium">Konto</th>
              <th scope="col" className="py-2 text-right font-medium">YTD-Anfang</th>
              {/* A-6/A-8: KEIN nackter „Ist"-Kopf über einer unvollständigen Größe.
                  `summe_ist` ist Σ der bisher AUSGEWIESENEN Monate (aktivierung.py
                  ::jahres_summe) und enthält `ytd_anfang` ausdrücklich NICHT — sie
                  ist damit weder ein Jahreswert noch der volle Zugang. Der Stichtag
                  stammt aus dem Snapshot, nicht aus einem hartkodierten Monat. */}
              <th scope="col" className="py-2 text-right font-medium">
                {stichtag ? `Σ Ist-Monate bis ${stichtag}` : 'Σ Ist-Monate'}
                <span title={AKT_TEXTE.summeIstTitle}>*</span>
              </th>
              {quarterKeys.map((qk) => (
                <th key={qk} scope="col" className="py-2 text-right font-medium">
                  {Q_LABEL[qk] ?? qk}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {konten.map((k) => (
              <tr key={k.konto} className="border-b border-border/60 last:border-0">
                <th scope="row" className="py-1.5 text-left font-normal">
                  <span className="tabular-nums text-text-muted">{k.konto}</span>{' '}
                  <span className="text-text">{k.label}</span>
                  {k.im_bau && (
                    <span className="ml-2 rounded border border-border bg-surface-2 px-1 text-[10px] font-normal text-text-muted">
                      im Bau
                    </span>
                  )}
                </th>
                <td className="py-1.5 text-right tabular-nums">{formatEur(k.ytd_anfang)}</td>
                <td className="py-1.5 text-right tabular-nums">
                  {k.summe_ist === null || k.summe_ist === undefined ? <NichtAbleitbar /> : formatEur(k.summe_ist)}
                </td>
                {quarterKeys.map((qk) => {
                  const q = k.quartale?.[qk];
                  return (
                    <td key={qk} className="py-1.5 text-right tabular-nums">
                      {!q ? DASH : q.ableitbar && q.wert !== null ? formatEur(q.wert) : <NichtAbleitbar />}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-xs text-text-muted">
          Nur abgeschlossene Quartale. „nicht ableitbar" = Monatswerte nicht
          eindeutig herleitbar (nicht 0,00 €, D-040).
        </p>
        <p className="mt-1 text-xs text-text-muted">{AKT_TEXTE.summeIstFussnote}</p>
      </div>
    </section>
  );
}
